/* eslint-disable react-refresh/only-export-components */
import { Fragment, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Search,
  ThumbsDown,
  ThumbsUp,
  Minus,
  X,
  Check,
  XCircle,
  UserX,
  UserCheck,
  ShieldBan,
  StickyNote,
  Pencil,
  Loader2,
  Link2,
  AlertTriangle,
} from 'lucide-react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import Layout from '@/components/Layout';
import { DisqualifyAccountDialog } from '@/components/campaigns/DisqualifyAccountDialog';
import {
  ADMIN_CPM_CAMPAIGN,
  ADMIN_CPM_CAMPAIGN_SUBMISSIONS,
  ADMIN_CPM_CAMPAIGN_SUBMISSION_STATS,
  ADMIN_REVIEW_CPM_CAMPAIGN_SUBMISSION,
  ADMIN_FORCE_UPDATE_CPM_CAMPAIGN_SUBMISSION_VIEW,
  ADMIN_FORCE_UPDATE_ALL_CPM_CAMPAIGN_SUBMISSION_VIEWS,
  ADMIN_SYNC_MISSING_CPM_CAMPAIGN_SUBMISSION_VIEWS,
  ADMIN_SET_CPM_CAMPAIGN_SUBMISSION_HASHTAG_VALIDATION,
  ADMIN_DISQUALIFY_CPM_CAMPAIGN_USER,
  ADMIN_UNDISQUALIFY_CPM_CAMPAIGN_USER,
  ADMIN_SET_CPM_CAMPAIGN_SUBMISSION_OBSERVATION,
  ADMIN_SET_CPM_CAMPAIGN_SUBMISSION_CPM_OVERRIDE,
  ADMIN_LINK_CPM_SUBMISSION_CONTENT,
  ADMIN_BULK_REVIEW_CPM_CAMPAIGN_SUBMISSIONS,
} from '@/queries/queries';
import { useMyDataQuery, useAdminResolveSuspectedDuplicateMutation } from '@/generated/graphql';
import {
  DISAPPROVE_REASON_CHIPS,
  resolveDisapproveReason,
} from '@/lib/campaigns/resolveDisapproveReason';
import { CurrencyMaskInput } from '@/components/MaskedInput';
import {
  effectiveCpmRateCents,
  platformCpmRateCents,
  getCpmOverrideEditorConfig,
} from '@/lib/campaigns/cpmOverrideOptions';
import {
  resolveSubmissionRefreshPlan,
  type AdminSubmissionAction,
} from '@/lib/campaigns/resolveSubmissionRefreshPlan';
import { getSubmissionVerdictKey } from '@/lib/campaigns/getSubmissionVerdictKey';
import { mapOfficialMetricsError } from '@/lib/campaigns/mapOfficialMetricsError';
import { mapReviewImagesError } from '@/lib/campaigns/mapReviewImagesError';
import { parseReviewImages, type ReviewImage } from '@/lib/campaigns/reviewImages';
import { initialDisapproveImages } from '@/lib/campaigns/initialDisapproveImages';
import { ReviewImagesField } from '@/components/campaigns/ReviewImagesField';
import { ReviewImagesStrip } from '@/components/campaigns/ReviewImagesStrip';
import { CampaignReportButton } from '@/components/campaigns/CampaignReportButton';
import { resolveSubmissionEarningsDisplay } from '@/lib/campaigns/resolveSubmissionEarningsDisplay';
import { resolveSubmissionEarningsHint } from '@/lib/campaigns/resolveSubmissionEarningsHint';
import { useToast } from '@/hooks/use-toast';
import { AccountReliabilityBadge } from '@/components/admin/AccountReliabilityBadge';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import type { ChartConfig } from '@/components/ui/chart';
import { formatDateTime, formatCompact } from '@/lib/format';
import { summarizeEffectiveCpm } from '@/lib/campaigns/summarizeEffectiveCpm';
import { describeCampaignCpmRates } from '@/lib/campaigns/describeCampaignCpmRates';
import { EffectiveCpmHint } from '@/components/campaigns/EffectiveCpmHint';
import CampaignHero from '@/components/CampaignHero';
import { RankingCampaignAudit } from '@/components/campaigns/RankingCampaignAudit';
import { CampaignContentEditor } from '@/components/campaigns/CampaignContentEditor';
import CampaignAppealsTab from '@/components/admin/CampaignAppealsTab';
import { TopEarningClipsCard } from '@/components/admin/TopEarningClipsCard';
import { TopEarningClipsExportButton } from '@/components/admin/TopEarningClipsExportButton';
import { TOP_EARNING_PAGE_SIZE, useCpmTopEarningClips } from '@/hooks/use-cpm-top-earning-clips';
import { useCpmSubmissionMediaDownload } from '@/hooks/use-cpm-submission-media-download';
import AdminCampaignChannelRequestsTab from './AdminCampaignChannelRequestsTab';

const APPROVAL_OPTIONS = ['neutral', 'approved', 'disapproved'] as const;
type Approval = typeof APPROVAL_OPTIONS[number];
type HashtagValidation = 'neutral' | 'validated' | 'invalidated';
type DisapproveChip = typeof DISAPPROVE_REASON_CHIPS[number];
const PLATFORM_FILTERS = ['youtube', 'instagram', 'tiktok'] as const;
const HASHTAG_FILTERS = ['neutral', 'validated', 'invalidated'] as const;

const PAGE_SIZE = 50;
const COL_SPAN = 10;

type ReviewGroupSubmission = {
  id: string;
  user_id: string;
  platform: string;
  url: string;
  admin_approval: string;
  content_review_group_id?: string | null;
  content_review_group_decision?: string | null;
};

type ReviewGroupValidation = 'minimum' | 'sameUser' | 'crossPlatform' | 'alreadyGrouped';

export function validateReviewGroupSelection(
  submissions: ReviewGroupSubmission[],
): ReviewGroupValidation | null {
  if (submissions.length < 2) return 'minimum';
  if (submissions.some((submission) => submission.content_review_group_id)) {
    return 'alreadyGrouped';
  }
  if (new Set(submissions.map((submission) => submission.user_id)).size !== 1) {
    return 'sameUser';
  }
  if (new Set(submissions.map((submission) => submission.platform)).size < 2) {
    return 'crossPlatform';
  }
  return null;
}

export function isSingleReviewBlocked(
  submission: ReviewGroupSubmission,
  approval: string,
) {
  return !!submission.content_review_group_id
    && approval !== submission.content_review_group_decision;
}

export function collectPendingBulkApproveIds(
  submissions: Array<{
    id: string;
    admin_approval?: string | null;
    pending_appeal_id?: string | null;
  }>,
  selectedIds: string[],
): string[] {
  const selected = new Set(selectedIds);
  return submissions
    .filter((submission) => (
      selected.has(submission.id)
      && submission.admin_approval === 'neutral'
      && !submission.pending_appeal_id
    ))
    .map((submission) => submission.id);
}

export async function executeReviewGroupLink({
  mutate,
  submissionIds,
  refetch,
}: {
  mutate: (options: { variables: { submissionIds: string[] } }) => Promise<any>;
  submissionIds: string[];
  refetch: () => Promise<any>;
}) {
  const result = await mutate({ variables: { submissionIds } });
  await refetch();
  return result.data?.adminLinkCpmSubmissionContent?.submission_ids?.length
    || submissionIds.length;
}

export function ReviewGroupBadge({ submission }: { submission: ReviewGroupSubmission }) {
  const { t } = useTranslation();
  if (!submission.content_review_group_id) return null;
  return (
    <Badge
      variant="outline"
      className="ml-1 mt-1 border-sky-500/40 text-[10px] text-sky-400"
    >
      {t('admin.campaigns.submissions.reviewGroup.badge')}
      {' · '}
      {submission.content_review_group_decision}
    </Badge>
  );
}

export function ReviewGroupConfirmationDialog({
  open,
  submissions,
  loading,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  submissions: ReviewGroupSubmission[];
  loading: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.campaigns.submissions.reviewGroup.dialogTitle')}</DialogTitle>
          <DialogDescription>
            {t('admin.campaigns.submissions.reviewGroup.approvalPrecedence')}
          </DialogDescription>
        </DialogHeader>
        <ul className="max-h-64 space-y-2 overflow-y-auto rounded-md border p-3">
          {submissions.map((submission) => (
            <li key={submission.id} className="space-y-0.5 text-sm">
              <span className="font-semibold uppercase text-primary">{submission.platform}</span>
              <p className="break-all font-mono text-xs text-muted-foreground">
                {submission.url}
              </p>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('admin.campaigns.submissions.reviewGroup.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCampaignSubmissionsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuditorContext = location.pathname.startsWith('/campaign-audit');
  const backPath = isAuditorContext ? '/campaign-audit' : `/admin/campaigns/${id}`;
  const { data, loading, error, refetch } = useQuery(ADMIN_CPM_CAMPAIGN, {
    variables: { id: id || '' }, skip: !id, fetchPolicy: 'no-cache', pollInterval: 60_000,
    context: { queryDeduplication: false },
  });
  const campaign = data?.adminCpmCampaign;
  if (loading) return <Layout><main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8"><p aria-busy="true">{t('campaign.loading')}</p></main></Layout>;
  if (error || !campaign || campaign.id !== id) return <Layout><main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8"><div role="alert" className="space-y-3"><p>{t('ranking.loadError')}</p><Button variant="outline" onClick={() => void refetch().catch(() => undefined)}>{t('ranking.retry')}</Button></div></main></Layout>;
  if (campaign.campaign_type !== 'ranking') return <CpmCampaignSubmissionsPage key={id} campaign={campaign} refetchCampaign={refetch} />;
  return <Layout><main className="min-w-0 flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8"><div className="mx-auto min-w-0 max-w-screen-2xl space-y-6">
    <div className="flex flex-wrap items-center justify-between gap-3"><div className="flex min-w-0 flex-wrap items-center gap-3"><Button variant="ghost" size="sm" onClick={() => navigate(backPath)}><ChevronLeft className="mr-1 h-4 w-4" />{t('common.back')}</Button><div><h1 className="text-xl font-semibold">{campaign.name}</h1><p className="text-sm text-muted-foreground">{t('ranking.audit.title')}</p></div></div><div className="flex flex-wrap gap-2"><CampaignContentEditor campaignId={campaign.id} onSaved={refetch} />{!isAuditorContext && <Button variant="outline" size="sm" onClick={() => navigate(`/admin/campaigns/${id}`)}>{t('admin.campaigns.edit.tabs.edit')}</Button>}<Button variant="outline" size="sm" onClick={() => navigate(isAuditorContext ? `/campaign-audit/${id}/participants` : `/admin/campaigns/${id}/participants`)}>{t('admin.campaigns.edit.tabs.participants')}</Button></div></div>
    <RankingCampaignAudit key={id} campaign={campaign} />
  </div></main></Layout>;
}

function CpmCampaignSubmissionsPage({ campaign, refetchCampaign }: { campaign: any; refetchCampaign: () => Promise<unknown> }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuditorContext = location.pathname.startsWith('/campaign-audit');
  const backPath = isAuditorContext ? '/campaign-audit' : `/admin/campaigns/${id}`;

  const [activeTab, setActiveTab] = useState<'submissions' | 'channels' | 'appeals'>('submissions');
  const [approvalFilter, setApprovalFilter] = useState<string>('');
  const [platform, setPlatform] = useState('');
  const [hashtag, setHashtag] = useState('');
  const [aging72, setAging72] = useState(false);
  const [suspectedOnly, setSuspectedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [selectedSubmissionIds, setSelectedSubmissionIds] = useState<string[]>([]);
  const [reviewGroupDialogOpen, setReviewGroupDialogOpen] = useState(false);
  const [bulkApproveDialogOpen, setBulkApproveDialogOpen] = useState(false);

  const [disapproveTarget, setDisapproveTarget] = useState<string | null>(null);
  const [disapproveChip, setDisapproveChip] = useState<DisapproveChip | null>(null);
  const [disapproveReason, setDisapproveReason] = useState('');
  const [disapproveImages, setDisapproveImages] = useState<ReviewImage[]>([]);
  const [disapproveImagesBusy, setDisapproveImagesBusy] = useState(false);

  const [observationTarget, setObservationTarget] = useState<string | null>(null);
  const [observationDraft, setObservationDraft] = useState('');

  const [cpmTarget, setCpmTarget] = useState<string | null>(null);
  const [cpmDraftCents, setCpmDraftCents] = useState<number | null>(null);

  const [banUserTarget, setBanUserTarget] = useState<string | null>(null); // user_id (documentId)
  const [banUserReason, setBanUserReason] = useState('');
  const [unbanUserTarget, setUnbanUserTarget] = useState<string | null>(null);

  const [disqualifyDuplicateTarget, setDisqualifyDuplicateTarget] = useState<string | null>(null);
  const [disqualifyAccountTarget, setDisqualifyAccountTarget] = useState<
    { submissionId: string; userId: string; userLabel: string | null } | null
  >(null);

  const { data, loading, error, refetch } = useQuery(ADMIN_CPM_CAMPAIGN_SUBMISSIONS, {
    variables: {
      campaignId: id || '',
      approval: (aging72 ? 'neutral' : approvalFilter) || undefined,
      search: search || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      suspectedDuplicatesOnly: suspectedOnly || undefined,
      platform: platform || undefined,
      validatedHashtags: hashtag || undefined,
      pendingOlderThanHours: aging72 ? 72 : undefined,
    },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });
  // Campaign-global aggregates, deliberately its own query: the backend builds
  // them by walking every eligible submission of the campaign, so tying them to
  // the row list made paging, filtering and every post-save refetch pay that
  // cost. Here they are re-read only when an action can have moved them.
  const {
    data: statsData,
    refetch: refetchStats,
  } = useQuery(ADMIN_CPM_CAMPAIGN_SUBMISSION_STATS, {
    variables: { campaignId: id || '' },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });

  const {
    ranking: topEarning,
    page: topEarningPage,
    setPage: setTopEarningPage,
    pageCount: topEarningPageCount,
    loading: topEarningLoading,
    error: topEarningError,
    refetch: refetchTopEarning,
  } = useCpmTopEarningClips(id || '');

  const currency = campaign?.currency || 'BRL';

  const { data: myData } = useMyDataQuery();
  const isPlatformAdmin = myData?.myData?.is_admin === true;
  const cpmEditor = getCpmOverrideEditorConfig({ campaign, isPlatformAdmin });

  const formatEarnings = (cents: number) => {
    const value = (cents || 0) / 100;
    return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };
   
  const effectiveEarningsCents = (s: any) => {
    if (s.disqualified) return 0;
    if (s.admin_approval !== 'approved') return 0;
    return s.accumulated_payout_cents || 0;
  };

  const [review, { loading: settingApproval }] = useMutation(ADMIN_REVIEW_CPM_CAMPAIGN_SUBMISSION);
  const [forceUpdateView] = useMutation(ADMIN_FORCE_UPDATE_CPM_CAMPAIGN_SUBMISSION_VIEW);
  const [forceUpdateAll, { loading: forcingUpdateAll }] = useMutation(ADMIN_FORCE_UPDATE_ALL_CPM_CAMPAIGN_SUBMISSION_VIEWS);
  const [syncMissing, { loading: syncingMissing }] = useMutation(ADMIN_SYNC_MISSING_CPM_CAMPAIGN_SUBMISSION_VIEWS);
  const [setHashtagValidation, { loading: settingHashtag }] = useMutation(ADMIN_SET_CPM_CAMPAIGN_SUBMISSION_HASHTAG_VALIDATION);
  const [setObservation, { loading: savingObservation }] = useMutation(ADMIN_SET_CPM_CAMPAIGN_SUBMISSION_OBSERVATION);
  const [setCpmOverride, { loading: savingCpmOverride }] =
    useMutation(ADMIN_SET_CPM_CAMPAIGN_SUBMISSION_CPM_OVERRIDE);
  const { downloadClip: downloadTopClip, rowState: topClipDownloadState } =
    useCpmSubmissionMediaDownload();
  const [forcingId, setForcingId] = useState<string | null>(null);
  const [approvingSubmissionId, setApprovingSubmissionId] = useState<string | null>(null);

  const [disqualifyUser, { loading: banningUser }] = useMutation(ADMIN_DISQUALIFY_CPM_CAMPAIGN_USER);
  const [undisqualifyUser, { loading: unbanningUser }] = useMutation(ADMIN_UNDISQUALIFY_CPM_CAMPAIGN_USER);
  const [resolveSuspected, { loading: resolvingSuspected }] = useAdminResolveSuspectedDuplicateMutation();
  const [linkSubmissionContent, { loading: linkingSubmissionContent }] = useMutation(
    ADMIN_LINK_CPM_SUBMISSION_CONTENT,
  );
  const [bulkReview, { loading: bulkApproving }] = useMutation(
    ADMIN_BULK_REVIEW_CPM_CAMPAIGN_SUBMISSIONS,
  );

   
  const submissions = ((data?.adminCpmCampaignSubmissions?.data as any[]) || []);
  const selectedSubmissions = submissions.filter((submission) => (
    selectedSubmissionIds.includes(submission.id)
  )) as ReviewGroupSubmission[];
  const bulkApproveIds = collectPendingBulkApproveIds(submissions, selectedSubmissionIds);
  const resolvedDisapprove = resolveDisapproveReason({
    chip: disapproveChip,
    otherText: disapproveReason,
  });
  const reviewGroupValidation = validateReviewGroupSelection(selectedSubmissions);
  const selectedUserId = selectedSubmissions[0]?.user_id || null;
  const total = data?.adminCpmCampaignSubmissions?.total ?? 0;
  const totalPages = data?.adminCpmCampaignSubmissions?.pageCount ?? 1;
  const stats = statsData?.adminCpmCampaignSubmissions?.stats as
    | {
        total_views: string;
        approved_views: string;
        paid_views: string;
        total_earnings_cents: number;
        total_submissions: number;
        daily_series: Array<{
          date: string;
          total_views: string;
          approved_paid_views: string;
          approved_unpaid_views: string;
          clip_count: number;
        }>;
      }
    | undefined;

  // Re-read exactly what the action invalidated. The mutations return the
  // updated submission, so Apollo's normalized cache refreshes the acted-on row
  // for free; `rows` is for actions that can rewrite OTHER rows too.
  const refreshAfter = async (action: AdminSubmissionAction) => {
    const plan = resolveSubmissionRefreshPlan(action);
    await Promise.all([
      plan.rows ? refetch() : null,
      plan.stats ? refetchStats() : null,
      plan.stats ? refetchTopEarning() : null,
      action === 'appealAccept' || action === 'appealKeep' || action === 'appealRequestMore'
        ? refetchCampaign()
        : null,
    ]);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleApprovalFilter = (val: string) => {
    setApprovalFilter(val);
    if (val !== 'neutral') setAging72(false);
    setPage(1);
  };

  const handlePlatformFilter = (value: string) => {
    setPlatform(value === 'all' ? '' : value);
    setPage(1);
  };

  const handleHashtagFilter = (value: string) => {
    setHashtag(value === 'all' ? '' : value);
    setPage(1);
  };

  const handleToggleAging72 = () => {
    const next = !aging72;
    setAging72(next);
    if (next) setApprovalFilter('neutral');
    setPage(1);
  };

  const handleToggleSuspectedOnly = () => {
    setSuspectedOnly((v) => !v);
    setPage(1);
  };

  const closeDisapprovePanel = () => {
    setDisapproveTarget(null);
    setDisapproveChip(null);
    setDisapproveReason('');
    setDisapproveImages([]);
    setDisapproveImagesBusy(false);
  };

  const handleSetApproval = async (
    subId: string,
    approval: Approval,
    reason?: string,
    previousApproval?: string,
    images?: ReviewImage[],
  ) => {
    const submission = submissions.find((candidate) => candidate.id === subId) as any;
    if (
      (approval === 'disapproved' || approval === 'neutral')
      && submission?.pending_appeal_id
    ) {
      toast({ title: t('admin.campaigns.submissions.appeals.pendingToast') });
      return;
    }
    setApprovingSubmissionId(subId);
    try {
      await review({
        variables: {
          id: subId,
          approval,
          reason: reason || undefined,
          // Images only travel with a disapproval; approve/neutral clear them server-side.
          ...(approval === 'disapproved' ? { images: images ?? [] } : {}),
        },
      });
      toast({ title: t('admin.campaigns.submissions.approvalSet') });
      closeDisapprovePanel();
      refreshAfter('approval');
      if (approval === 'approved' && previousApproval !== 'approved') {
         
        const existing = (submissions.find((s: any) => s.id === subId)?.admin_observation as string | null | undefined) ?? null;
        openObservationPanel(subId, existing);
      }
     
    } catch (err: any) {
      const imagesErrorKey = mapReviewImagesError(err);
      toast({
        title: imagesErrorKey ? t(imagesErrorKey) : err?.message || t('admin.campaigns.submissions.approvalError'),
        variant: 'destructive',
      });
    } finally {
      setApprovingSubmissionId(null);
    }
  };

  const handleApprovalClick = (subId: string, approval: Approval, previousApproval: string) => {
    const submission = submissions.find((candidate) => candidate.id === subId);
    if (submission && isSingleReviewBlocked(submission, approval)) {
      toast({
        title: t('admin.campaigns.submissions.reviewGroup.singleReviewError'),
        variant: 'destructive',
      });
      return;
    }
    if (
      (approval === 'disapproved' || approval === 'neutral')
      && (submission as any)?.pending_appeal_id
    ) {
      toast({ title: t('admin.campaigns.submissions.appeals.pendingToast') });
      return;
    }
    if (approval === 'disapproved') {
      // Reopening the same panel must not reset in-progress images/uploads.
      if (disapproveTarget === subId) return;
      setDisapproveTarget(subId);
      setDisapproveChip(null);
      setDisapproveReason('');
      setDisapproveImages(initialDisapproveImages(submission));
      setDisapproveImagesBusy(false);
      return;
    }
    handleSetApproval(subId, approval, undefined, previousApproval);
  };

  const handleConfirmDisapprove = (subId: string) => {
    if (!resolvedDisapprove.ok) {
      toast({ title: t('admin.campaigns.submissions.reasonRequired') });
      return;
    }
    handleSetApproval(subId, 'disapproved', resolvedDisapprove.reason, undefined, disapproveImages);
  };

  const handleToggleReviewGroupSubmission = (submissionId: string, checked: boolean) => {
    setSelectedSubmissionIds((current) => (
      checked
        ? Array.from(new Set([...current, submissionId]))
        : current.filter((candidate) => candidate !== submissionId)
    ));
  };

  const handleLinkSubmissionContent = async () => {
    if (reviewGroupValidation) return;
    try {
      const linkedCount = await executeReviewGroupLink({
        mutate: linkSubmissionContent,
        submissionIds: selectedSubmissionIds,
        refetch: () => refreshAfter('linkReviewGroup'),
      });
      toast({
        title: t('admin.campaigns.submissions.reviewGroup.success', { count: linkedCount }),
      });
      setSelectedSubmissionIds([]);
      setReviewGroupDialogOpen(false);
    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.submissions.reviewGroup.error'),
        variant: 'destructive',
      });
    }
  };

  const handleBulkApprove = async () => {
    if (bulkApproveIds.length === 0) return;
    try {
      const result = await bulkReview({ variables: { ids: bulkApproveIds } });
      const payload = result.data?.adminBulkReviewCpmCampaignSubmissions;
      toast({
        title: t('admin.campaigns.submissions.bulkApproveResult', {
          approved: payload?.approved ?? 0,
          skipped: payload?.skipped?.length ?? 0,
        }),
      });
      await refreshAfter('bulkApprove');
      setSelectedSubmissionIds([]);
      setBulkApproveDialogOpen(false);
    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.submissions.approvalError'),
        variant: 'destructive',
      });
    }
  };

  const handleSetHashtagValidation = async (subId: string, validated_hashtags: HashtagValidation) => {
    try {
      await setHashtagValidation({
        variables: { id: subId, validated_hashtags },
        // This mutation answers Boolean!, so unlike the others there is no
        // submission in the response for Apollo to normalize. Write the one
        // changed field into the cached row directly rather than re-reading the
        // whole list for it.
        update: (cache) => {
          cache.writeFragment({
            id: cache.identify({ __typename: 'AdminCpmCampaignSubmission', id: subId }),
            fragment: gql`
              fragment SubmissionHashtagValidation on AdminCpmCampaignSubmission {
                validated_hashtags
              }
            `,
            data: { __typename: 'AdminCpmCampaignSubmission', validated_hashtags },
          });
        },
      });
      toast({ title: t('admin.campaigns.submissions.hashtagValidationSet') });
      refreshAfter('hashtagValidation');

    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.submissions.hashtagValidationError'),
        variant: 'destructive',
      });
    }
  };

  const openObservationPanel = (subId: string, current: string | null | undefined) => {
    setObservationTarget(subId);
    setObservationDraft(current || '');
  };

  const closeObservationPanel = () => {
    setObservationTarget(null);
    setObservationDraft('');
  };

  const handleSaveObservation = async (subId: string) => {
    try {
      await setObservation({ variables: { id: subId, observation: observationDraft } });
      toast({ title: t('admin.campaigns.submissions.observation.saved') });
      closeObservationPanel();
      refreshAfter('observation');
     
    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.submissions.observation.error'),
        variant: 'destructive',
      });
    }
  };

  const handleRemoveObservation = async (subId: string) => {
    try {
      await setObservation({ variables: { id: subId, observation: '' } });
      toast({ title: t('admin.campaigns.submissions.observation.cleared') });
      closeObservationPanel();
      refreshAfter('observation');

    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.submissions.observation.error'),
        variant: 'destructive',
      });
    }
  };

  const openCpmPanel = (subId: string, currentOverride: number | null | undefined) => {
    setCpmTarget(subId);
    setCpmDraftCents(Number.isInteger(currentOverride) ? (currentOverride as number) : null);
  };

  const closeCpmPanel = () => {
    setCpmTarget(null);
    setCpmDraftCents(null);
  };

  const handleSaveCpmOverride = async (subId: string, cents: number | null) => {
    try {
      await setCpmOverride({ variables: { id: subId, cpm_rate_cents: cents } });
      toast({ title: t('admin.campaigns.submissions.cpmOverride.saved') });
      closeCpmPanel();
      refreshAfter('cpmOverride');

    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.submissions.cpmOverride.error'),
        variant: 'destructive',
      });
    }
  };

  const doForceUpdate = async (subId: string) => {
    try {
      setForcingId(subId);
      await forceUpdateView({ variables: { id: subId } });
      toast({ title: t('admin.campaigns.submissions.forceUpdateQueued') });
     
    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.submissions.forceUpdateError'),
        variant: 'destructive',
      });
    } finally {
      setForcingId(null);
    }
  };

  const doForceUpdateAll = async () => {
    if (!id || forcingUpdateAll) return;
    try {
      const result = await forceUpdateAll({ variables: { campaignId: id } });
      const count = result.data?.adminForceUpdateAllCpmCampaignSubmissionViews ?? 0;
      toast({ title: t('admin.campaigns.submissions.forceUpdateAllQueued', { count }) });
     
    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.submissions.forceUpdateAllError'),
        variant: 'destructive',
      });
    }
  };

  const doSyncMissing = async () => {
    if (!id || syncingMissing) return;
    try {
      const result = await syncMissing({ variables: { campaignId: id } });
      const count = result.data?.adminSyncMissingCpmCampaignSubmissionViews ?? 0;
      toast({
        title: count > 0
          ? t('admin.campaigns.submissions.syncMissingQueued', { count })
          : t('admin.campaigns.submissions.syncMissingNone'),
      });
     
    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.submissions.syncMissingError'),
        variant: 'destructive',
      });
    }
  };

  const handleDisqualifyUser = async (userId: string, reason: string) => {
    if (!id || !reason.trim()) return;
    try {
      const res = await disqualifyUser({ variables: { campaignId: id, userId, reason: reason.trim() } });
      const reclaimed = res?.data?.adminDisqualifyCpmCampaignUser?.reclaimed_cents ?? 0;
      toast({
        title: t('admin.campaigns.submissions.banUserSuccess'),
        description: reclaimed > 0
          ? t('admin.campaigns.submissions.banUserReclaimed', { amount: formatEarnings(reclaimed) })
          : undefined,
      });
      setBanUserTarget(null);
      setBanUserReason('');
      refreshAfter('disqualifyUser');
     
    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.submissions.banUserError'),
        variant: 'destructive',
      });
    }
  };

  const handleUndisqualifyUser = async (userId: string) => {
    if (!id) return;
    try {
      await undisqualifyUser({ variables: { campaignId: id, userId } });
      toast({ title: t('admin.campaigns.submissions.unbanUserSuccess') });
      setUnbanUserTarget(null);
      refreshAfter('undisqualifyUser');
     
    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.submissions.unbanUserError'),
        variant: 'destructive',
      });
    }
  };

  const handleResolveSuspected = async (subId: string, action: 'CLEAR' | 'DISQUALIFY') => {
    try {
      await resolveSuspected({ variables: { id: subId, action } });
      toast({
        title: action === 'CLEAR'
          ? t('admin.campaigns.submissions.resolveClearedSuccess')
          : t('admin.campaigns.submissions.resolveDisqualifiedSuccess'),
      });
      setDisqualifyDuplicateTarget(null);
      refreshAfter('resolveSuspectedDuplicate');
    } catch (err: any) {
      toast({
        title: err?.message || t('admin.campaigns.submissions.resolveFailed'),
        variant: 'destructive',
      });
    }
  };

  // Aggregates are computed server-side across all eligible submissions
  // (non-disqualified, non-disapproved) regardless of the page's filters
  // and pagination. See `computeAggregateStats` in the backend service.
  const eligibleSubmissionCount = stats?.total_submissions ?? 0;
  const latestTotalViews = Number.parseInt(stats?.total_views || '0', 10) || 0;
  const totalEarningsCents = stats?.total_earnings_cents ?? 0;
  const avgViewsPerSubmission = eligibleSubmissionCount > 0
    ? Math.round(latestTotalViews / eligibleSubmissionCount)
    : 0;
  const parseViews = (value: string | null | undefined) => Number.parseInt(value || '0', 10) || 0;
  const approvedViews = parseViews(stats?.approved_views);
  const paidViews = parseViews(stats?.paid_views);
  const nominalRates = describeCampaignCpmRates({
    cpm_rate_cents: campaign?.cpm_rate_cents ?? 0,
    cpm_platform_rates: campaign?.cpm_platform_rates,
    allowed_platforms: campaign?.allowed_platforms,
  });
  // With per-network rates there is no single nominal CPM to compare against.
  const nominalCpmCents = nominalRates.kind === 'single' ? nominalRates.rateCents : 0;
  const effectiveCpm = summarizeEffectiveCpm({
    earningsCents: totalEarningsCents,
    approvedViews,
    paidViews,
    nominalCpmCents,
  });
  const accrualWindowHours = campaign?.view_accrual_window_hours ?? 72;
  const formatCpm = (cents: number | null) => (
    cents === null ? '—' : formatEarnings(cents)
  );
  const formatPercent = (value: number | null) => (
    value === null ? '—' : `${Math.round(value * 100)}%`
  );
  const timeSeries = (stats?.daily_series || []).map((p) => ({
    date: p.date,
    total_views: parseViews(p.total_views),
    approved_paid_views: parseViews(p.approved_paid_views),
    approved_unpaid_views: parseViews(p.approved_unpaid_views),
    clip_count: p.clip_count,
  }));


  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-screen-2xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(backPath)}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('common.back')}
              </Button>
              <div className="flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-primary" />
                <div>
                  <h1 className="text-xl font-bold">
                    {campaign?.name || t('admin.campaigns.submissions.title')}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {total} {t('admin.campaigns.submissions.countLabel')}
                  </p>
                </div>
              </div>
            </div>
            {id && <CampaignContentEditor campaignId={id} onSaved={refetchCampaign} />}
            {id && isAuditorContext && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/campaign-audit/${id}/participants`)}
              >
                {t('admin.campaigns.edit.tabs.participants')}
              </Button>
            )}
            {id && !isAuditorContext && (
              <div className="flex items-center gap-1 border border-border rounded-md p-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/admin/campaigns/${id}`)}
                >
                  {t('admin.campaigns.edit.tabs.edit')}
                </Button>
                <Button variant="default" size="sm" disabled>
                  {t('admin.campaigns.edit.tabs.submissions')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/admin/campaigns/${id}/participants`)}
                >
                  {t('admin.campaigns.edit.tabs.participants')}
                </Button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {id && <CampaignReportButton campaignId={id} disabled={!campaign || campaign.id !== id} />}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={syncingMissing}>
                    <RefreshCw className={`w-4 h-4 mr-1 ${syncingMissing ? 'animate-spin' : ''}`} />
                    {t('admin.campaigns.submissions.syncMissing')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('admin.campaigns.submissions.syncMissing')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('admin.campaigns.submissions.syncMissingConfirm')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction onClick={doSyncMissing}>{t('common.confirm')}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={forcingUpdateAll}>
                    <RefreshCw className={`w-4 h-4 mr-1 ${forcingUpdateAll ? 'animate-spin' : ''}`} />
                    {t('admin.campaigns.submissions.forceUpdateAll')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('admin.campaigns.submissions.forceUpdateAll')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('admin.campaigns.submissions.forceUpdateAllConfirm')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction onClick={doForceUpdateAll}>{t('common.confirm')}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Badge variant="outline" className="text-red-500 border-red-500">ADMIN</Badge>
            </div>
          </div>

          <CampaignHero campaign={campaign} />

          {/* Summary cards */}
          {eligibleSubmissionCount > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4 pb-3 px-4">
                  <p className="text-xs text-muted-foreground">
                    {t('admin.campaigns.submissions.summary.totalViews')}
                  </p>
                  <p className="text-2xl font-bold">{formatCompact(latestTotalViews)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 px-4">
                  <p className="text-xs text-muted-foreground">
                    {t('admin.campaigns.submissions.summary.totalSubmissions')}
                  </p>
                  <p className="text-2xl font-bold">{total.toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 px-4">
                  <p className="text-xs text-muted-foreground">
                    {t('admin.campaigns.submissions.summary.avgViews')}
                  </p>
                  <p className="text-2xl font-bold">{formatCompact(avgViewsPerSubmission)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 px-4">
                  <p className="text-xs text-muted-foreground">
                    {t('admin.campaigns.submissions.summary.totalEarnings')}
                  </p>
                  <p className="text-2xl font-bold">{formatEarnings(totalEarningsCents)}</p>
                </CardContent>
              </Card>
              <Card data-testid="cpm-paid-views-card">
                <CardContent className="pt-4 pb-3 px-4">
                  <p className="text-xs text-muted-foreground">
                    {t('admin.campaigns.submissions.summary.paidViews')}
                  </p>
                  <p className="text-2xl font-bold">{formatCompact(paidViews)}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('admin.campaigns.submissions.summary.paidViewsHint', {
                      share: formatPercent(effectiveCpm.paidShare),
                      approved: formatCompact(approvedViews),
                    })}
                  </p>
                </CardContent>
              </Card>
              <Card data-testid="cpm-after-window-views-card">
                <CardContent className="pt-4 pb-3 px-4">
                  <p className="text-xs text-muted-foreground">
                    {accrualWindowHours === 0
                      ? t('admin.campaigns.submissions.summary.afterWindowViewsUnlimited')
                      : t('admin.campaigns.submissions.summary.afterWindowViews', {
                        hours: accrualWindowHours,
                      })}
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCompact(Math.max(0, approvedViews - paidViews))}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('admin.campaigns.submissions.summary.afterWindowViewsHint')}
                  </p>
                </CardContent>
              </Card>
              <Card data-testid="cpm-effective-card">
                <CardContent className="pt-4 pb-3 px-4">
                  <p className="text-xs text-muted-foreground">
                    {t('admin.campaigns.submissions.summary.effectiveCpm')}
                  </p>
                  <p className="text-2xl font-bold">{formatCpm(effectiveCpm.effectiveCpmCents)}</p>
                  <EffectiveCpmHint
                    rates={nominalRates}
                    currency={campaign?.currency || 'BRL'}
                    efficiency={effectiveCpm.efficiency}
                  />
                </CardContent>
              </Card>
              <Card data-testid="cpm-paid-cpm-card">
                <CardContent className="pt-4 pb-3 px-4">
                  <p className="text-xs text-muted-foreground">
                    {t('admin.campaigns.submissions.summary.paidCpm')}
                  </p>
                  <p className="text-2xl font-bold">{formatCpm(effectiveCpm.paidCpmCents)}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('admin.campaigns.submissions.summary.paidCpmHint')}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Views growth & submissions chart */}
          {timeSeries.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('admin.campaigns.submissions.chart.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  config={{
                    approved_paid_views: {
                      label: t('admin.campaigns.submissions.chart.approvedPaidViews'),
                      color: '#2E7D32',
                    },
                    approved_unpaid_views: {
                      label: t('admin.campaigns.submissions.chart.approvedUnpaidViews'),
                      color: '#A5D6A7',
                    },
                    clip_count: {
                      label: t('admin.campaigns.submissions.chart.newSubmissions'),
                      color: 'hsl(var(--chart-2))',
                    },
                  } satisfies ChartConfig}
                  className="h-[280px] w-full"
                >
                  <ComposedChart data={timeSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis
                      yAxisId="left"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1_000 ? `${(v / 1_000).toFixed(0)}K` : v}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 11 }}
                      allowDecimals={false}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Bar
                      yAxisId="left"
                      dataKey="approved_paid_views"
                      stackId="views"
                      fill="var(--color-approved_paid_views)"
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="approved_unpaid_views"
                      stackId="views"
                      fill="var(--color-approved_unpaid_views)"
                      radius={[4, 4, 0, 0]}
                    />
                    <Line
                      yAxisId="right"
                      type="linear"
                      dataKey="clip_count"
                      stroke="#4ade80"
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: '#4ade80', strokeWidth: 0 }}
                      connectNulls
                    />
                  </ComposedChart>
                </ChartContainer>
              </CardContent>
            </Card>
          )}

          <TopEarningClipsCard
            clips={topEarning?.data || []}
            total={topEarning?.total}
            page={topEarningPage}
            pageSize={TOP_EARNING_PAGE_SIZE}
            pageCount={topEarningPageCount}
            onPageChange={setTopEarningPage}
            loading={topEarningLoading}
            loadError={!!topEarningError}
            onRetry={() => { void refetchTopEarning().catch(() => {}); }}
            headerActions={<TopEarningClipsExportButton campaignId={id || ''} currency={currency} />}
            formatEarnings={formatEarnings}
            downloadState={topClipDownloadState}
            onDownloadClip={downloadTopClip}
          />

          {/* In-page tabs: submissions / channel requests / appeals */}
          <div className="flex items-center gap-1 border border-border rounded-md p-1 w-fit">
            <Button
              variant={activeTab === 'submissions' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('submissions')}
            >
              {t('admin.campaigns.edit.tabs.submissions')}
            </Button>
            <Button
              variant={activeTab === 'channels' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('channels')}
            >
              {t('admin.campaigns.channelRequests.tabWithCount', {
                count: campaign?.pending_channel_requests_count ?? 0,
              })}
            </Button>
            <Button
              variant={activeTab === 'appeals' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('appeals')}
            >
              {t('admin.campaigns.submissions.appeals.tab')}
              {(campaign?.pending_appeals_count ?? 0) > 0 && (
                <Badge
                  variant={activeTab === 'appeals' ? 'secondary' : 'destructive'}
                  className="ml-1.5 h-5 min-w-5 px-1.5 text-[10px]"
                >
                  {campaign?.pending_appeals_count}
                </Badge>
              )}
            </Button>
          </div>

          {activeTab === 'channels' && id && (
            <Card>
              <CardContent className="pt-6">
                <AdminCampaignChannelRequestsTab campaignId={id} />
              </CardContent>
            </Card>
          )}

          {activeTab === 'appeals' && id && (
            <CampaignAppealsTab campaignId={id} refreshAfter={refreshAfter} />
          )}

          {/* Submissions table */}
          {activeTab === 'submissions' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <CardTitle>{t('admin.campaigns.submissions.title')}</CardTitle>
                  {selectedSubmissionIds.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary">
                        {t('admin.campaigns.submissions.reviewGroup.selected', {
                          count: selectedSubmissionIds.length,
                        })}
                      </Badge>
                      <Button
                        size="sm"
                        onClick={() => setReviewGroupDialogOpen(true)}
                        disabled={!!reviewGroupValidation}
                      >
                        <Link2 className="mr-1 h-4 w-4" />
                        {t('admin.campaigns.submissions.reviewGroup.link')}
                      </Button>
                      {bulkApproveIds.length > 0 && (
                        <Button
                          size="sm"
                          onClick={() => setBulkApproveDialogOpen(true)}
                          disabled={bulkApproving}
                        >
                          {bulkApproving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                          {t('admin.campaigns.submissions.bulkApprove', {
                            count: bulkApproveIds.length,
                          })}
                        </Button>
                      )}
                      {reviewGroupValidation && (
                        <span className="text-xs text-amber-500">
                          {t(`admin.campaigns.submissions.reviewGroup.validation.${reviewGroupValidation}`)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t('admin.campaigns.submissions.filtersLabel')}:
                    </span>
                    <div className="flex items-center gap-1 border border-border rounded-md p-1">
                      {['', ...APPROVAL_OPTIONS].map((val) => (
                        <Button
                          key={val || 'all'}
                          variant={approvalFilter === val ? 'default' : 'ghost'}
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleApprovalFilter(val)}
                        >
                          {val === '' ? t('admin.campaigns.submissions.filters.all')
                            : val === 'neutral' ? t('admin.campaigns.submissions.filters.pending')
                            : val === 'approved' ? t('admin.campaigns.submissions.filters.approved')
                            : t('admin.campaigns.submissions.filters.disapproved')}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t('admin.campaigns.submissions.filters.platform')}
                    </span>
                    <Select value={platform || 'all'} onValueChange={handlePlatformFilter}>
                      <SelectTrigger className="h-9 w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          {t('admin.campaigns.submissions.filters.all')}
                        </SelectItem>
                        {PLATFORM_FILTERS.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`campaign.channels.platformLabel.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t('admin.campaigns.submissions.filters.hashtags')}
                    </span>
                    <Select value={hashtag || 'all'} onValueChange={handleHashtagFilter}>
                      <SelectTrigger className="h-9 w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          {t('admin.campaigns.submissions.filters.all')}
                        </SelectItem>
                        {HASHTAG_FILTERS.map((value) => (
                          <SelectItem key={value} value={value}>
                            {value === 'neutral'
                              ? t('admin.campaigns.submissions.hashtagNeutral')
                              : value === 'validated'
                                ? t('admin.campaigns.submissions.hashtagValidated')
                                : t('admin.campaigns.submissions.hashtagInvalidated')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant={aging72 ? 'default' : 'outline'}
                    size="sm"
                    className="h-9"
                    onClick={handleToggleAging72}
                  >
                    {t('admin.campaigns.submissions.filters.aging')}
                  </Button>
                  <Button
                    variant={suspectedOnly ? 'default' : 'outline'}
                    size="sm"
                    className="h-9"
                    onClick={handleToggleSuspectedOnly}
                  >
                    {t('admin.campaigns.submissions.filters.suspectedDuplicates')}
                  </Button>
                  <form onSubmit={handleSearch} className="flex gap-1">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder={t('admin.campaigns.submissions.searchPlaceholder')}
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="pl-8 max-w-xs h-9"
                      />
                    </div>
                    <Button type="submit" variant="outline" size="sm">
                      <Search className="w-4 h-4" />
                    </Button>
                  </form>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading && !data ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">
                  {t('admin.common.error')}
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <span className="sr-only">
                              {t('admin.campaigns.submissions.reviewGroup.selectColumn')}
                            </span>
                          </TableHead>
                          <TableHead>{t('admin.campaigns.submissions.table.platform')}</TableHead>
                          <TableHead>{t('admin.campaigns.submissions.table.url')}</TableHead>
                          <TableHead>{t('admin.campaigns.submissions.table.submitter')}</TableHead>
                          <TableHead>{t('admin.campaigns.submissions.table.views')}</TableHead>
                          <TableHead>{t('admin.campaigns.submissions.table.cpm')}</TableHead>
                          <TableHead>{t('admin.campaigns.submissions.table.earned')}</TableHead>
                          <TableHead>{t('admin.campaigns.submissions.table.hashtags')}</TableHead>
                          <TableHead>{t('admin.campaigns.submissions.table.submittedAt')}</TableHead>
                          <TableHead className="w-[160px]">
                            {t('admin.campaigns.submissions.table.actions')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {submissions.map((s) => {
                          const views = Number.parseInt(s.latest_views || s.last_counted_views || '0', 10) || 0;
                          const minViewsThreshold = campaign?.min_views_threshold || 0;
                          const verdictKey = getSubmissionVerdictKey(s);
                          const metricsErrorKey = mapOfficialMetricsError(s.metrics_error);
                          const earnings = resolveSubmissionEarningsDisplay({
                            verdict: verdictKey,
                            views,
                            accumulatedPayoutCents: s.accumulated_payout_cents,
                            cpmRateCents: effectiveCpmRateCents(
                              {
                                cpm_rate_cents: campaign?.cpm_rate_cents ?? 0,
                                cpm_platform_rates: campaign?.cpm_platform_rates,
                              },
                              s,
                            ),
                            minViewsThreshold,
                            maxPayoutPerClipCents: campaign?.max_payout_per_clip_cents,
                          });
                          const earningsHint = resolveSubmissionEarningsHint({
                            earningsKind: earnings.kind,
                            earnedCents: earnings.cents,
                            maxPayoutPerClipCents: campaign?.max_payout_per_clip_cents,
                            viewsRemainingToThreshold: earnings.viewsRemainingToThreshold,
                            minViewsThreshold,
                            remainingBudgetCents: campaign?.budget_remaining_cents,
                            submittedAt: s.createdAt,
                            windowHours: campaign?.view_accrual_window_hours,
                            now: new Date(),
                          });
                          const isApproving = approvingSubmissionId === s.id;
                          const rowActionDisabled = settingApproval || forcingId === s.id || isApproving;
                          const rowClass = s.disqualified || s.admin_approval === 'disapproved' ? 'opacity-60' : '';
                          const selected = selectedSubmissionIds.includes(s.id);
                          const reviewGroupSelectionDisabled = !!s.content_review_group_id
                            || (!!selectedUserId && selectedUserId !== s.user_id && !selected);
                          return (
                            <Fragment key={s.id}>
                              <TableRow className={rowClass} data-testid={`submission-row-${s.id}`}>
                                <TableCell>
                                  <Checkbox
                                    checked={selected}
                                    disabled={reviewGroupSelectionDisabled}
                                    onCheckedChange={(checked) => handleToggleReviewGroupSubmission(
                                      s.id,
                                      checked === true,
                                    )}
                                    aria-label={t(
                                      'admin.campaigns.submissions.reviewGroup.selectLabel',
                                      { url: s.url },
                                    )}
                                  />
                                </TableCell>
                                <TableCell>
                                  <span className="text-xs font-semibold uppercase px-2 py-1 rounded bg-primary/10 text-primary">
                                    {s.platform}
                                  </span>
                                </TableCell>
                                <TableCell className="max-w-xs">
                                  <a
                                    href={s.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-sm text-blue-400 hover:underline truncate"
                                  >
                                    <ExternalLink className="w-3 h-3 shrink-0" />
                                    <span className="truncate">{s.url}</span>
                                  </a>
                                  {s.suspected_duplicate && (
                                    <Badge
                                      variant="outline"
                                      className="text-amber-500 border-amber-500/40 text-[10px] mt-1"
                                      title={s.suspected_duplicate_reason || undefined}
                                    >
                                      {t('admin.campaigns.submissions.suspectedDuplicateBadge')}
                                    </Badge>
                                  )}
                                  <ReviewGroupBadge submission={s} />
                                  {(s as any).pending_appeal_id && (
                                    <Badge
                                      variant="outline"
                                      className="text-amber-500 border-amber-500/40 text-[10px] mt-1"
                                    >
                                      {t('admin.campaigns.submissions.appeals.contestingBadge')}
                                    </Badge>
                                  )}
                                  {s.admin_approval === 'disapproved' && s.approval_reason && (
                                    <p className="text-xs text-red-400 mt-0.5">
                                      {t('admin.campaigns.submissions.reasonLabel')}: {s.approval_reason}
                                    </p>
                                  )}
                                  {s.admin_approval === 'disapproved' && (
                                    <ReviewImagesStrip
                                      images={parseReviewImages(s.rejection_images)}
                                      showVisibility
                                      className="mt-1"
                                    />
                                  )}
                                  {s.disqualified && s.disqualification_reason && (
                                    <p className="text-xs text-red-400 mt-0.5">
                                      {t('admin.campaigns.submissions.disqualifiedBadge')}: {s.disqualification_reason}
                                    </p>
                                  )}
                                  {metricsErrorKey && (
                                    <p className="text-xs text-amber-400 mt-0.5">
                                      {t(metricsErrorKey)}
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="text-sm">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="font-medium">{s.user_name || '—'}</p>
                                      <AccountReliabilityBadge reliability={s.account_reliability} />
                                      {s.user_disqualified && (
                                        <Badge variant="destructive" className="text-[10px]">
                                          {t('admin.campaigns.submissions.userDisqualifiedBadge')}
                                        </Badge>
                                      )}
                                      {s.user_id && !s.user_disqualified && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0 text-red-500"
                                          onClick={() => { setBanUserTarget(s.user_id); setBanUserReason(''); }}
                                          title={t('admin.campaigns.submissions.actions.disqualifyUser')}
                                        >
                                          <UserX className="w-3.5 h-3.5" />
                                        </Button>
                                      )}
                                      {s.user_id && !s.user_disqualified && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0 text-red-500"
                                          data-testid="submission-disqualify-account"
                                          onClick={() => setDisqualifyAccountTarget({
                                            submissionId: s.id,
                                            userId: s.user_id,
                                            userLabel: s.user_name || s.user_email || null,
                                          })}
                                          title={t('admin.campaigns.accountDisqualification.action')}
                                          aria-label={t('admin.campaigns.accountDisqualification.action')}
                                        >
                                          <ShieldBan className="w-3.5 h-3.5" />
                                        </Button>
                                      )}
                                      {s.user_id && s.user_disqualified && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0 text-emerald-500"
                                          onClick={() => setUnbanUserTarget(s.user_id)}
                                          title={t('admin.campaigns.submissions.actions.undisqualifyUser')}
                                        >
                                          <UserCheck className="w-3.5 h-3.5" />
                                        </Button>
                                      )}
                                    </div>
                                    <p className="text-xs text-muted-foreground">{s.user_email || '—'}</p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div>
                                    <span>{views.toLocaleString()}</span>
                                    {minViewsThreshold > 0 && (
                                      <p
                                        className={`text-[10px] leading-snug ${
                                          views < minViewsThreshold
                                            ? 'text-amber-400'
                                            : 'text-muted-foreground'
                                        }`}
                                      >
                                        {views < minViewsThreshold
                                          ? t('campaign.submissions.viewsUntilMin', {
                                            count: minViewsThreshold - views,
                                            threshold: minViewsThreshold.toLocaleString(),
                                          })
                                          : t('campaign.minViewsShort', {
                                            count: minViewsThreshold.toLocaleString(),
                                          })}
                                      </p>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const overridden = Number.isInteger(s.cpm_rate_override_cents);
                                    const rate = effectiveCpmRateCents(
                                      {
                                        cpm_rate_cents: campaign?.cpm_rate_cents ?? 0,
                                        cpm_platform_rates: campaign?.cpm_platform_rates,
                                      },
                                      s,
                                    );
                                    const label = (
                                      <span className={overridden ? 'text-amber-400 font-semibold' : 'text-muted-foreground'}>
                                        {formatEarnings(rate)}
                                      </span>
                                    );
                                    if (!cpmEditor.canEdit) return label;
                                    return (
                                      <button
                                        type="button"
                                        className="inline-flex items-center gap-1 hover:underline"
                                        title={t('admin.campaigns.submissions.cpmOverride.title')}
                                        onClick={() => openCpmPanel(s.id, s.cpm_rate_override_cents)}
                                      >
                                        {label}
                                        <Pencil className="w-3 h-3 text-muted-foreground shrink-0" />
                                      </button>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell className="font-medium">
                                  <div>
                                    {formatEarnings(effectiveEarningsCents(s))}
                                    {earningsHint.kind === 'viewsRemainingToEarn' && (
                                      <p className="text-[10px] font-normal text-amber-400 leading-snug">
                                        {t('campaign.submissions.viewsRemainingToEarn', {
                                          count: earningsHint.count,
                                          threshold: earningsHint.threshold.toLocaleString(),
                                        })}
                                      </p>
                                    )}
                                    {earningsHint.kind === 'missedThresholdInWindow' && (
                                      <p className="text-[10px] font-normal text-amber-400 leading-snug">
                                        {t('campaign.submissions.missedThresholdInWindow', {
                                          threshold: earningsHint.threshold.toLocaleString(),
                                          hours: earningsHint.windowHours,
                                        })}
                                      </p>
                                    )}
                                    {earningsHint.kind === 'budgetExhausted' && (
                                      <p className="text-[10px] font-normal text-amber-400 leading-snug">
                                        {t('campaign.submissions.budgetExhaustedApprovedWarning')}
                                      </p>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-0.5">
                                    <Button
                                      variant={s.validated_hashtags === 'validated' ? 'default' : 'ghost'}
                                      size="sm"
                                      className={`h-6 w-6 p-0 ${s.validated_hashtags === 'validated' ? 'text-gray-900' : 'text-emerald-500'}`}
                                      onClick={() => handleSetHashtagValidation(s.id, 'validated')}
                                      disabled={settingHashtag}
                                      title={t('admin.campaigns.submissions.hashtagValidated')}
                                    >
                                      <Check className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className={`h-6 w-6 p-0 ${s.validated_hashtags === 'neutral' ? 'bg-gray-500/20 text-gray-300' : 'text-muted-foreground'}`}
                                      onClick={() => handleSetHashtagValidation(s.id, 'neutral')}
                                      disabled={settingHashtag}
                                      title={t('admin.campaigns.submissions.hashtagNeutral')}
                                    >
                                      <Minus className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant={s.validated_hashtags === 'invalidated' ? 'destructive' : 'ghost'}
                                      size="sm"
                                      className={`h-6 w-6 p-0 ${s.validated_hashtags === 'invalidated' ? '' : 'text-red-500'}`}
                                      onClick={() => handleSetHashtagValidation(s.id, 'invalidated')}
                                      disabled={settingHashtag}
                                      title={t('admin.campaigns.submissions.hashtagInvalidated')}
                                    >
                                      <XCircle className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                                  {formatDateTime(s.createdAt)}
                                </TableCell>
                                <TableCell>
                                  {s.pre_approval === 'pre_approved' && (
                                    <div
                                      className="mb-1 inline-flex items-center gap-1 whitespace-nowrap rounded border border-sky-500/40 bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-400"
                                      title={t('admin.campaigns.submissions.preApproval.tooltip')}
                                    >
                                      {t('admin.campaigns.submissions.preApproval.badge')}
                                      {typeof s.pre_approval_score === 'number' && (
                                        <span className="font-normal normal-case text-sky-300">
                                          {t('admin.campaigns.submissions.preApproval.score', { score: s.pre_approval_score })}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {s.pre_approval === 'ineligible' && (
                                    <div
                                      className="mb-1 inline-flex items-center gap-1 whitespace-nowrap rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-400"
                                      title={t('admin.campaigns.submissions.preApproval.tooltip')}
                                    >
                                      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                                      {t('admin.campaigns.submissions.preApproval.ineligibleBadge')}
                                      {typeof s.pre_approval_score === 'number' && (
                                        <span className="font-normal normal-case text-amber-300">
                                          {t('admin.campaigns.submissions.preApproval.score', { score: s.pre_approval_score })}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  <div className="flex items-center gap-0.5 flex-wrap">
                                  <Button
                                    variant={s.admin_approval === 'approved' ? 'default' : 'ghost'}
                                    size="sm"
                                    className={s.admin_approval === 'approved' ? 'text-gray-900' : 'text-emerald-500'}
                                    onClick={() => handleApprovalClick(s.id, 'approved', s.admin_approval || 'neutral')}
                                    disabled={rowActionDisabled}
                                    title={t('admin.campaigns.submissions.actions.approve')}
                                  >
                                    {isApproving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={s.admin_approval === 'neutral' ? 'bg-gray-500/20 text-gray-300' : 'text-muted-foreground'}
                                    onClick={() => handleApprovalClick(s.id, 'neutral', s.admin_approval || 'neutral')}
                                    disabled={rowActionDisabled}
                                    title={t('admin.campaigns.submissions.filters.pending')}
                                  >
                                    {isApproving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Minus className="w-4 h-4" />}
                                  </Button>
                                  <Button
                                    variant={s.admin_approval === 'disapproved' ? 'destructive' : 'ghost'}
                                    size="sm"
                                    className={s.admin_approval === 'disapproved' ? '' : 'text-red-500'}
                                    onClick={() => handleApprovalClick(s.id, 'disapproved', s.admin_approval || 'neutral')}
                                    disabled={rowActionDisabled}
                                    title={t('admin.campaigns.submissions.actions.disapprove')}
                                  >
                                    {isApproving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4" />}
                                  </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-blue-400"
                                      onClick={() => doForceUpdate(s.id)}
                                      disabled={rowActionDisabled}
                                      title={t('admin.campaigns.submissions.actions.forceUpdate')}
                                    >
                                      <RefreshCw className={`w-4 h-4 ${forcingId === s.id ? 'animate-spin' : ''}`} />
                                    </Button>
                                    {s.admin_approval === 'approved' && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={s.admin_observation ? 'text-amber-400' : 'text-muted-foreground'}
                                        onClick={() => openObservationPanel(s.id, s.admin_observation)}
                                        title={t('admin.campaigns.submissions.actions.editObservation')}
                                      >
                                        <StickyNote className="w-4 h-4" />
                                      </Button>
                                    )}
                                    {s.suspected_duplicate && (
                                      <>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-7 px-2 text-xs"
                                          onClick={() => handleResolveSuspected(s.id, 'CLEAR')}
                                          disabled={resolvingSuspected}
                                        >
                                          {t('admin.campaigns.submissions.notDuplicate')}
                                        </Button>
                                        <Button
                                          variant="destructive"
                                          size="sm"
                                          className="h-7 px-2 text-xs"
                                          onClick={() => setDisqualifyDuplicateTarget(s.id)}
                                          disabled={resolvingSuspected}
                                        >
                                          {t('admin.campaigns.submissions.disqualifyDuplicate')}
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                              {disapproveTarget === s.id && (
                                <TableRow>
                                  <TableCell colSpan={COL_SPAN}>
                                    <div className="border border-red-500/30 rounded-lg p-3 space-y-2 bg-red-500/5">
                                      <p className="text-xs font-medium text-red-400">
                                        {t('admin.campaigns.submissions.disapproveConfirm')}
                                      </p>
                                      <div className="space-y-1">
                                        <Label className="text-xs">
                                          {t('admin.campaigns.submissions.reasonLabel')}
                                        </Label>
                                        <div className="flex flex-wrap gap-1">
                                          {DISAPPROVE_REASON_CHIPS.map((chip) => (
                                            <Button
                                              key={chip}
                                              type="button"
                                              variant={disapproveChip === chip ? 'destructive' : 'outline'}
                                              size="sm"
                                              className="h-7 px-2 text-xs"
                                              onClick={() => {
                                                setDisapproveChip(chip);
                                                if (chip !== 'other') setDisapproveReason('');
                                              }}
                                            >
                                              {t(`admin.campaigns.submissions.reasonChips.${chip}`)}
                                            </Button>
                                          ))}
                                        </div>
                                        {disapproveChip === 'other' && (
                                          <Textarea
                                            value={disapproveReason}
                                            onChange={(e) => setDisapproveReason(e.target.value)}
                                            placeholder={t('admin.campaigns.submissions.reasonPlaceholder')}
                                            rows={2}
                                          />
                                        )}
                                      </div>
                                      <ReviewImagesField
                                        value={disapproveImages}
                                        onChange={setDisapproveImages}
                                        disabled={rowActionDisabled}
                                        onBusyChange={setDisapproveImagesBusy}
                                      />
                                      <div className="flex gap-2">
                                        <Button
                                          variant="destructive"
                                          size="sm"
                                          onClick={() => handleConfirmDisapprove(s.id)}
                                          disabled={rowActionDisabled || !resolvedDisapprove.ok || disapproveImagesBusy}
                                        >
                                          {isApproving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ThumbsDown className="w-3 h-3 mr-1" />}
                                          {t('admin.campaigns.submissions.actions.disapprove')}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={closeDisapprovePanel}
                                        >
                                          <X className="w-3 h-3 mr-1" />
                                          {t('common.cancel')}
                                        </Button>
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                              {observationTarget === s.id && (
                                <TableRow>
                                  <TableCell colSpan={COL_SPAN}>
                                    <div className="border border-amber-500/30 rounded-lg p-3 space-y-2 bg-amber-500/5">
                                      <p className="text-xs font-medium text-amber-400">
                                        {t('admin.campaigns.submissions.observation.title')}
                                      </p>
                                      <div className="space-y-1">
                                        <Label className="text-xs">
                                          {t('admin.campaigns.submissions.observation.label')}
                                        </Label>
                                        <Textarea
                                          value={observationDraft}
                                          onChange={(e) => setObservationDraft(e.target.value)}
                                          placeholder={t('admin.campaigns.submissions.observation.placeholder')}
                                          rows={3}
                                        />
                                      </div>
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          onClick={() => handleSaveObservation(s.id)}
                                          disabled={savingObservation}
                                        >
                                          <Check className="w-3 h-3 mr-1" />
                                          {t('admin.campaigns.submissions.observation.save')}
                                        </Button>
                                        {s.admin_observation && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-red-500"
                                            onClick={() => handleRemoveObservation(s.id)}
                                            disabled={savingObservation}
                                          >
                                            <X className="w-3 h-3 mr-1" />
                                            {t('admin.campaigns.submissions.observation.remove')}
                                          </Button>
                                        )}
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={closeObservationPanel}
                                        >
                                          <X className="w-3 h-3 mr-1" />
                                          {t('common.cancel')}
                                        </Button>
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                              {cpmTarget === s.id && cpmEditor.canEdit && (
                                <TableRow>
                                  <TableCell colSpan={COL_SPAN}>
                                    <div className="border border-sky-500/30 rounded-lg p-3 space-y-2 bg-sky-500/5">
                                      <p className="text-xs font-medium text-sky-400">
                                        {t('admin.campaigns.submissions.cpmOverride.title')}
                                      </p>
                                      {cpmEditor.presetsCents.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                          {cpmEditor.presetsCents.map((cents) => (
                                            <Button
                                              key={cents}
                                              size="sm"
                                              variant={cents === s.cpm_rate_override_cents ? 'default' : 'outline'}
                                              disabled={savingCpmOverride}
                                              onClick={() => handleSaveCpmOverride(s.id, cents)}
                                            >
                                              {formatEarnings(cents)}
                                            </Button>
                                          ))}
                                        </div>
                                      )}
                                      {cpmEditor.freeInput && (
                                        <div className="space-y-1">
                                          <div className="flex items-center gap-2 max-w-sm">
                                            <CurrencyMaskInput
                                              valueCents={cpmDraftCents}
                                              onChangeCents={(c) => setCpmDraftCents(c)}
                                              currency={currency}
                                              allowEmpty
                                            />
                                            <Button
                                              size="sm"
                                              disabled={savingCpmOverride || cpmDraftCents == null}
                                              onClick={() => handleSaveCpmOverride(s.id, cpmDraftCents)}
                                            >
                                              <Check className="w-3 h-3 mr-1" />
                                              {t('admin.campaigns.submissions.cpmOverride.save')}
                                            </Button>
                                          </div>
                                          {cpmEditor.freeInput.maxCents != null && (
                                            <p className="text-[11px] text-muted-foreground">
                                              {t('admin.campaigns.submissions.cpmOverride.boundsHint', {
                                                min: formatEarnings(cpmEditor.freeInput.minCents),
                                                max: formatEarnings(cpmEditor.freeInput.maxCents),
                                              })}
                                            </p>
                                          )}
                                        </div>
                                      )}
                                      <div className="flex gap-2">
                                        {Number.isInteger(s.cpm_rate_override_cents) && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-amber-400"
                                            disabled={savingCpmOverride}
                                            onClick={() => handleSaveCpmOverride(s.id, null)}
                                          >
                                            {t('admin.campaigns.submissions.cpmOverride.resetToBase', {
                                              value: formatEarnings(platformCpmRateCents(
                                                {
                                                  cpm_rate_cents: campaign?.cpm_rate_cents ?? 0,
                                                  cpm_platform_rates: campaign?.cpm_platform_rates,
                                                },
                                                s.platform,
                                              )),
                                            })}
                                          </Button>
                                        )}
                                        <Button size="sm" variant="ghost" onClick={closeCpmPanel}>
                                          <X className="w-3 h-3 mr-1" />
                                          {t('common.cancel')}
                                        </Button>
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                              {banUserTarget === s.user_id && (
                                <TableRow>
                                  <TableCell colSpan={COL_SPAN}>
                                    <div className="border border-red-500/30 rounded-lg p-3 space-y-2 bg-red-500/5">
                                      <p className="text-xs font-medium text-red-400">
                                        {t('admin.campaigns.submissions.banUserConfirm')}
                                      </p>
                                      <div className="space-y-1">
                                        <Label className="text-xs">
                                          {t('admin.campaigns.submissions.reasonLabel')}
                                        </Label>
                                        <Textarea
                                          value={banUserReason}
                                          onChange={(e) => setBanUserReason(e.target.value)}
                                          placeholder={t('admin.campaigns.submissions.reasonPlaceholder')}
                                          rows={2}
                                        />
                                      </div>
                                      <div className="flex gap-2">
                                        <Button
                                          variant="destructive"
                                          size="sm"
                                          onClick={() => handleDisqualifyUser(s.user_id, banUserReason)}
                                          disabled={banningUser || !banUserReason.trim()}
                                        >
                                          <UserX className="w-3 h-3 mr-1" />
                                          {t('admin.campaigns.submissions.actions.disqualifyUser')}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => { setBanUserTarget(null); setBanUserReason(''); }}
                                        >
                                          <X className="w-3 h-3 mr-1" />
                                          {t('common.cancel')}
                                        </Button>
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>
                          );
                        })}
                        {submissions.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={COL_SPAN} className="text-center py-10 text-muted-foreground">
                              {t('admin.common.noResults')}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      {t('admin.common.page')} {page} {t('admin.common.of')} {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          )}
        </div>
      </main>
      <ReviewGroupConfirmationDialog
        open={reviewGroupDialogOpen}
        submissions={selectedSubmissions}
        loading={linkingSubmissionContent}
        onOpenChange={setReviewGroupDialogOpen}
        onConfirm={handleLinkSubmissionContent}
      />
      <AlertDialog
        open={bulkApproveDialogOpen}
        onOpenChange={(open) => { if (!open) setBulkApproveDialogOpen(false); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('admin.campaigns.submissions.bulkApprove', { count: bulkApproveIds.length })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.campaigns.submissions.bulkApproveConfirm', { count: bulkApproveIds.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkApproving || bulkApproveIds.length === 0}
              onClick={() => { void handleBulkApprove(); }}
            >
              {t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={!!unbanUserTarget}
        onOpenChange={(open) => { if (!open) setUnbanUserTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('admin.campaigns.submissions.actions.undisqualifyUser')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.campaigns.submissions.unbanUserConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={unbanningUser}
              onClick={() => unbanUserTarget && handleUndisqualifyUser(unbanUserTarget)}
            >
              {t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <DisqualifyAccountDialog
        submissionId={disqualifyAccountTarget?.submissionId ?? null}
        currency={currency}
        user={id && disqualifyAccountTarget ? {
          campaignId: id,
          userId: disqualifyAccountTarget.userId,
          label: disqualifyAccountTarget.userLabel,
        } : null}
        onOpenChange={(open) => { if (!open) setDisqualifyAccountTarget(null); }}
        onDone={(scope) => { void refreshAfter(scope === 'user' ? 'disqualifyUser' : 'disqualifyAccount'); }}
      />
      <AlertDialog
        open={!!disqualifyDuplicateTarget}
        onOpenChange={(open) => { if (!open) setDisqualifyDuplicateTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('admin.campaigns.submissions.disqualifyDuplicate')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.campaigns.submissions.disqualifyDuplicateConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={resolvingSuspected}
              onClick={() => disqualifyDuplicateTarget && handleResolveSuspected(disqualifyDuplicateTarget, 'DISQUALIFY')}
            >
              {t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
